// ============================================================
// Firmen-Akte — Besprechungsnotizen aus Google Calendar und
// E-Mails aus Gmail automatisch den Mandanten zuordnen.
// Dedup über quelle_id (Event-/Message-ID): mehrfacher Import
// erzeugt keine Duplikate.
// ============================================================

import { db, type AkteEintrag, type Mandant } from '../db/schema';
import { loadMandanten } from './mandantenService';
import { listEvents, isTokenValid, type GoogleEvent } from './googleCalendar';
import { listInboxMessages } from './gmailService';
import { logAktion } from './auditLogService';
import type { MandantID } from '../types/branded';

export interface ImportErgebnis {
  neu: number;
  uebersprungen: number;   // ohne Firmen-Zuordnung
  duplikate: number;
}

// ── Firmen-Zuordnung ───────────────────────────────────────────────────────

/** Termin → Mandant über title_aliases + location_keywords (Substring, case-insensitive). */
function matchMandantForEvent(ev: GoogleEvent, mandanten: Mandant[]): MandantID | null {
  const haystack = `${ev.summary ?? ''} ${ev.location ?? ''} ${ev.description ?? ''}`.toLowerCase();
  let best: { id: MandantID; score: number } | null = null;
  for (const m of mandanten) {
    let score = 0;
    for (const alias of m.title_aliases) {
      if (alias.length >= 3 && haystack.includes(alias.toLowerCase())) score += 3;
    }
    for (const kw of m.location_keywords) {
      if (kw.length >= 3 && haystack.includes(kw.toLowerCase())) score += 1;
    }
    if (score > 0 && (best === null || score > best.score)) {
      best = { id: m.id, score };
    }
  }
  return best?.id ?? null;
}

/** E-Mail-Absender → Mandant über email_domains (Suffix-Match). */
function matchMandantForAddress(address: string, mandanten: Mandant[]): MandantID | null {
  const domain = address.split('@')[1]?.toLowerCase();
  if (domain === undefined) return null;
  for (const m of mandanten) {
    for (const d of m.email_domains) {
      const dl = d.toLowerCase();
      if (domain === dl || domain.endsWith(`.${dl}`)) return m.id;
    }
  }
  return null;
}

// ── Import: Besprechungsnotizen aus Google Calendar ───────────────────────

/**
 * Holt Termine der letzten `daysBack` Tage (Standard 30) aus dem Kalender
 * und legt für jeden Termin MIT Notiz (description) und Firmen-Treffer
 * einen Akten-Eintrag an.
 */
export async function importBesprechungsnotizen(
  calendarId: string,
  daysBack = 30,
): Promise<ImportErgebnis> {
  if (!isTokenValid()) {
    throw new Error('Google Calendar nicht verbunden — bitte im Kalender-Modul anmelden.');
  }
  const mandanten = await loadMandanten();
  const from = new Date(); from.setDate(from.getDate() - daysBack);
  const to = new Date(); to.setDate(to.getDate() + 1);
  const events = await listEvents(calendarId, from.toISOString(), to.toISOString());

  const ergebnis: ImportErgebnis = { neu: 0, uebersprungen: 0, duplikate: 0 };

  for (const ev of events) {
    const notiz = (ev.description ?? '').trim();
    if (notiz === '') continue; // Termin ohne Notiz → keine Besprechungsnotiz

    const quelleId = `gcal:${ev.id}`;
    const existing = await db.akte.where('quelle_id').equals(quelleId).first();
    if (existing !== undefined) { ergebnis.duplikate++; continue; }

    const mandantId = matchMandantForEvent(ev, mandanten);
    if (mandantId === null) { ergebnis.uebersprungen++; continue; }

    await db.akte.add({
      id: crypto.randomUUID(),
      schema_version: 1,
      mandant_id: mandantId,
      typ: 'BESPRECHUNG',
      titel: ev.summary ?? '(ohne Titel)',
      inhalt: notiz,
      datum: ev.start.dateTime ?? ev.start.date ?? new Date().toISOString(),
      quelle_id: quelleId,
      erstellt_am: new Date().toISOString(),
    });
    ergebnis.neu++;
  }

  if (ergebnis.neu > 0) {
    void logAktion('SYSTEM', 'AKTE_KALENDER_IMPORT', { neu: ergebnis.neu });
  }
  return ergebnis;
}

// ── Import: E-Mails aus Gmail ──────────────────────────────────────────────

/**
 * Liest den Posteingang (Standard 30 Tage) und legt für jede E-Mail,
 * deren Absender-Domain zu einem Mandanten passt, einen Akten-Eintrag an.
 */
export async function importEmails(daysBack = 30): Promise<ImportErgebnis> {
  const mandanten = await loadMandanten();
  const messages = await listInboxMessages(daysBack);

  const ergebnis: ImportErgebnis = { neu: 0, uebersprungen: 0, duplikate: 0 };

  for (const msg of messages) {
    const quelleId = `gmail:${msg.id}`;
    const existing = await db.akte.where('quelle_id').equals(quelleId).first();
    if (existing !== undefined) { ergebnis.duplikate++; continue; }

    const mandantId = matchMandantForAddress(msg.from, mandanten);
    if (mandantId === null) { ergebnis.uebersprungen++; continue; }

    await db.akte.add({
      id: crypto.randomUUID(),
      schema_version: 1,
      mandant_id: mandantId,
      typ: 'EMAIL',
      titel: msg.subject,
      inhalt: msg.body,
      datum: msg.date,
      quelle_id: quelleId,
      absender: msg.from,
      erstellt_am: new Date().toISOString(),
    });
    ergebnis.neu++;
  }

  if (ergebnis.neu > 0) {
    void logAktion('SYSTEM', 'AKTE_EMAIL_IMPORT', { neu: ergebnis.neu });
  }
  return ergebnis;
}

// ── CRUD ───────────────────────────────────────────────────────────────────

export async function loadAkte(mandantId?: MandantID | null): Promise<AkteEintrag[]> {
  const all = mandantId !== undefined && mandantId !== null
    ? await db.akte.where('mandant_id').equals(mandantId).toArray()
    : await db.akte.toArray();
  return all.sort((a, b) => b.datum.localeCompare(a.datum));
}

export async function addManuellerEintrag(input: {
  mandant_id: MandantID;
  titel: string;
  inhalt: string;
  datum?: string;
}): Promise<void> {
  await db.akte.add({
    id: crypto.randomUUID(),
    schema_version: 1,
    mandant_id: input.mandant_id,
    typ: 'DOKUMENT',
    titel: input.titel,
    inhalt: input.inhalt,
    datum: input.datum ?? new Date().toISOString(),
    erstellt_am: new Date().toISOString(),
  });
  void logAktion('SYSTEM', 'AKTE_EINTRAG_ERSTELLT');
}

export async function deleteAkteEintrag(id: string): Promise<void> {
  await db.akte.delete(id);
  void logAktion('SYSTEM', 'AKTE_EINTRAG_GELOESCHT');
}

/** Dokument mit Datei-Anhang direkt anlegen (z.B. aus der Dokumente-Übersicht).
 *  mandant_id kann eine Firma ODER eine Sonderkategorie (Allgemein/Privat) sein. */
export async function addDokument(input: {
  mandant_id: string; titel: string; b64: string; mime: string;
}): Promise<void> {
  await db.akte.add({
    id: crypto.randomUUID(),
    schema_version: 1,
    mandant_id: input.mandant_id as MandantID,
    typ: 'DOKUMENT',
    titel: input.titel.trim() || 'Dokument',
    inhalt: '',
    datum: new Date().toISOString().slice(0, 10),
    anhang_b64: input.b64,
    anhang_mime: input.mime,
    erstellt_am: new Date().toISOString(),
  });
  void logAktion('SYSTEM', 'AKTE_DOKUMENT_ERSTELLT');
}

/** Zuordnung eines Akte-Eintrags ändern (Firma oder Sonderkategorie). */
export async function setAkteMandant(id: string, mandant_id: string): Promise<void> {
  const e = await db.akte.get(id);
  if (e !== undefined) {
    await db.akte.put({ ...e, mandant_id: mandant_id as MandantID });
    void logAktion('SYSTEM', 'AKTE_ZUORDNUNG_GEAENDERT');
  }
}

/** Folge-Aktion als erledigt / offen markieren (Aufgabenliste). */
export async function toggleAkteErledigt(id: string): Promise<void> {
  const e = await db.akte.get(id);
  if (e !== undefined) {
    await db.akte.put({ ...e, erledigt: !(e.erledigt ?? false) });
  }
}

// ── Belege → CSV für den Steuerberater ─────────────────────────────────────

function csvEscape(s: string): string {
  return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function centsToDecimal(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}

/**
 * Erzeugt eine Steuerberater-CSV aller Belege (österreichisches Format:
 * Semikolon-Trennung, Komma-Dezimal, BOM für Excel). Optional nur die
 * noch offenen (nicht erledigten) Belege.
 */
export async function belegeAlsCsv(
  mandantId: MandantID | null | undefined,
  nurOffene: boolean,
  mandantName: (id: string) => string,
): Promise<string> {
  const alle = await loadAkte(mandantId);
  const belege = alle
    .filter(e => e.typ === 'BELEG')
    .filter(e => !nurOffene || !(e.erledigt ?? false))
    .sort((a, b) => a.datum.localeCompare(b.datum));

  const header = ['Datum', 'Firma', 'Titel', 'Betrag', 'Erledigt'];
  const zeilen = belege.map(e => [
    e.datum.slice(0, 10),
    mandantName(e.mandant_id),
    e.titel,
    e.betrag_cents !== undefined ? centsToDecimal(e.betrag_cents) : '',
    (e.erledigt ?? false) ? 'ja' : 'offen',
  ].map(csvEscape).join(';'));

  return '﻿' + [header.join(';'), ...zeilen].join('\r\n');
}

export function downloadCsv(inhalt: string, dateiname: string): void {
  const blob = new Blob([inhalt], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = dateiname; a.click();
  URL.revokeObjectURL(url);
}
